import { useMemo, useState } from 'react';
import { noop } from 'lodash';
import type { MutationResult } from '@apollo/client';
import type { StripeError } from '@stripe/stripe-js';
import { state as reactiveState } from '@frameio/graphql-client';
import type {
  PaymentCard,
  UpdatePaymentCardMutation,
} from '@frameio/graphql-client/generated';
import type { SubscriptionUpdateSource } from '../../feature-gate/config';
import { useGetStripeDataFromElements } from './useGetStripeDataFromElements';
import type { TrackingBillingFieldsUpdated } from './useUpdatePaymentCard';
import useUpdatePaymentCard from './useUpdatePaymentCard';

type SubmitStripeForm = [
  submitStripeForm: () => Promise<void>,
  options: {
    apolloError: MutationResult<UpdatePaymentCardMutation>['error'];
    isLoading: boolean;
    reset: () => void;
    stripeError?: StripeError;
  },
];

type Options = {
  includeCard?: boolean;
  onCompleted?: () => void;
  onSubmit?: () => void;
  paymentCard?: PaymentCard;
  shouldSetAddressInReactiveState?: boolean;
  subscriptionUpdateSource?: SubscriptionUpdateSource;
  trackingBillingFieldsUpdated: TrackingBillingFieldsUpdated;
};

/**
 * Submits the Stripe form and updates the account cache with the new payment information
 * @param onCompleted - callback function to be called when the mutation is completed
 * @param includeCard - boolean indicating if the card should be included in the update
 * @param paymentCard - the payment card that is already set
 * @param shouldSetAddressInReactiveState - boolean indicating if the address should be set in reactive state instead of updating the Stripe account
 * @param trackingBillingFieldsUpdated - array of strings indicating which fields were updated for tracking purposes
 * @returns - a tuple containing the submitStripeForm function and the options object
 */
export default function useSubmitStripeForm({
  includeCard = false,
  onCompleted = noop,
  onSubmit = noop,
  paymentCard,
  shouldSetAddressInReactiveState = false,
  subscriptionUpdateSource,
  trackingBillingFieldsUpdated,
}: Options): SubmitStripeForm {
  const [stripeError, setStripeError] = useState<StripeError | undefined>();
  const [getData, { stripe, elements }] = useGetStripeDataFromElements(
    paymentCard,
    includeCard
  );

  const [updatePaymentCard, { error: apolloError, loading, reset }] =
    useUpdatePaymentCard(onCompleted);

  return useMemo(
    () => [
      async (): Promise<void> => {
        if (onSubmit) {
          onSubmit();
        }
        const data = await getData();

        if (data?.stripeError) {
          setStripeError(data.stripeError);
          return;
        }

        if (shouldSetAddressInReactiveState) {
          reactiveState.billing.stripe.set({
            addressElementValue: data?.addressElementValue,
            stripeTokenId: data?.stripeTokenId,
          });

          onCompleted();
          return;
        }

        updatePaymentCard({
          addressElementValue: data?.addressElementValue,
          stripeTokenId: data?.stripeTokenId,
          subscriptionUpdateSource,
          trackingBillingFieldsUpdated,
        });
      },
      {
        apolloError,
        isLoading: !stripe || !elements || loading,
        reset,
        stripeError,
      },
    ],
    [
      apolloError,
      elements,
      getData,
      loading,
      onCompleted,
      onSubmit,
      reset,
      shouldSetAddressInReactiveState,
      stripe,
      stripeError,
      subscriptionUpdateSource,
      trackingBillingFieldsUpdated,
      updatePaymentCard,
    ]
  );
}
